import { useMemo, useRef, useState, useEffect } from "react";
import { useGetExportListV2Query, useLazyGetExportListV2Query } from "../../../../../services/ExportV2/exportV2API";
import { useMockData } from "../../../../../common/util/useMockData";
import { useAppSelector } from "../../../../../store";
import {
  STATUS_DONE,
  STATUS_ERROR,
  STATUS_IN_PROGRESS,
  STATUS_PENDING,
} from "../../../../../common/constants/export-constants";
import type { ExportNotifyResponse } from "../../../../../services/ExportV2/exportV2API.types";

const mockData: ExportNotifyResponse = {
  Exports: [
    {
      ExportID: "a-zqmtRvg",
      ExportStatus: "DONE",
      Done: true,
    },
  ],
  ExportCount: 1,
  VisibleExportRequestCount: 1,
};

const FAST_POLL_INTERVAL_MS = 5000;
const DEFAULT_POLL_INTERVAL_MS = 30000;

export const useExportNotificationData = (moduleId: string, isExportDisabled: boolean) => {
  const useMock = useMockData();
  const { user } = useAppSelector((state) => state.boot);
  const exportModules = useAppSelector((state) => state.exports.modules);
  const currentAffAbo = user?.aff && user?.abo ? `${user.aff}-${user.abo}` : "";

  const hasInProgressExports = useMemo(() => {
    const moduleExports = exportModules[moduleId]?.Exports ?? [];
    return moduleExports.some((exp) => exp.ExportStatus === STATUS_IN_PROGRESS || exp.ExportStatus === STATUS_PENDING);
  }, [exportModules, moduleId]);

  const [hasPollingError, setHasPollingError] = useState(false);

  const pollingInterval = hasPollingError ? 0 : hasInProgressExports ? FAST_POLL_INTERVAL_MS : DEFAULT_POLL_INTERVAL_MS;

  const {
    data: exportListData,
    isError,
    refetch,
  } = useGetExportListV2Query(
    { moduleId },
    {
      pollingInterval,
      skip: isExportDisabled || useMock || !currentAffAbo,
      refetchOnMountOrArgChange: true,
    },
  );

  // Stop polling when the query errors out
  useEffect(() => {
    if (isError) {
      setHasPollingError(true);
    }
  }, [isError]);

  const [lazyExportListQuery] = useLazyGetExportListV2Query();

  // Track export IDs that were already completed on first load so we
  // don't show stale "completed" notifications for old exports.
  const initialCompletedIdsRef = useRef<Set<string> | null>(null);

  const data: ExportNotifyResponse | undefined = useMemo(() => {
    if (!exportListData) {
      return undefined;
    }

    const completedExports = exportListData.Exports.filter(
      (exp) => exp.ExportStatus === STATUS_DONE || exp.ExportStatus === STATUS_ERROR,
    );

    // On the very first data load, snapshot which exports are already
    // completed — these existed before the user triggered anything in
    // this session and should not produce a toast.
    if (initialCompletedIdsRef.current === null) {
      initialCompletedIdsRef.current = new Set(completedExports.map((exp) => exp.ExportID));
    }

    // Map all completed exports with a flag indicating if they were initial
    const allCompletedExports = completedExports.map((exp) => ({
      ExportID: exp.ExportID,
      ExportStatus: exp.ExportStatus,
      Done: exp.ExportStatus === STATUS_DONE,
      IsInitialExport: initialCompletedIdsRef.current?.has(exp.ExportID) || false,
    }));

    const doneExportCount = allCompletedExports.filter((exp) => exp.Done).length;

    return {
      Exports: allCompletedExports,
      ExportCount: doneExportCount,
      VisibleExportRequestCount: exportListData.Exports.length,
    };
  }, [exportListData]);

  const exportNotificationQuery = useMemo(() => {
    if (useMock) {
      return () => Promise.resolve(mockData);
    }
    return async (params: { moduleId: string }, preferCacheValue: boolean) => {
      return lazyExportListQuery({ moduleId: params.moduleId }, preferCacheValue);
    };
  }, [useMock, lazyExportListQuery]);

  return {
    data: useMock ? mockData : data,
    exportNotificationQuery,
    refetch,
    currentAffAbo,
    hasInProgressExports,
    isError,
  };
};
